import { motion } from 'framer-motion'
import type { RadarRay } from '@/services/api/teacher'
import { useAnimationConfig } from '../hooks/useReducedMotion'
import { polarToCartesian } from '../utils/geometry'

interface OrganicRaysProps {
  rays: RadarRay[]
  width: number
  height: number
  /** bubblenew-v2: Ray-Labels unterdrücken, wenn der Badge-Ring (Admin) sie ersetzt */
  hideLabels?: boolean
}

/**
 * Organic Rays Component - FIXED VERSION
 * Renders curved SVG rays with gradient transparency
 * Uses Bezier curves for organic feel
 * 
 * Phase 1.1: Fixed CSS Custom Properties - Uses CSS classes instead of inline vars
 */
export function OrganicRays({ rays, width, height, hideLabels = false }: OrganicRaysProps) {
  const animConfig = useAnimationConfig()

  const centerX = width / 2
  const centerY = height / 2
  const maxRadius = Math.min(width, height) / 2 - 60

  // Calculate curve offset for organic feel
  const curveAmount = 0.05 // Subtle curve

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
      style={{
        zIndex: 1,
      }}
    >
      <defs>
        {/* Gradient definitions for each ray */}
        {rays.map((ray, i) => (
          <linearGradient
            key={`ray-gradient-${ray.id || i}`}
            id={`ray-gradient-${i}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
            gradientUnits="userSpaceOnUse"
          >
            <stop
              offset="0%"
              stopColor="var(--ray-color-start)"
              stopOpacity="0"
            />
            <stop
              offset="30%"
              stopColor="var(--ray-color-mid)"
              stopOpacity="0.2"
            />
            <stop
              offset="100%"
              stopColor="var(--ray-color-end)"
              stopOpacity="0.6"
            />
          </linearGradient>
        ))}
      </defs>

      {rays.map((ray, i) => {
        const angle = ray.angle_degrees

        // Calculate start and end points
        // Rays converge at center (0,0). NOW label (z-index: 50) renders above them (z-index: 1).
        const start = polarToCartesian(angle, 0, centerX, centerY)
        const end = polarToCartesian(angle, maxRadius, centerX, centerY)

        // Calculate control point for Bezier curve
        // Create subtle organic curve
        const midX = (start.x + end.x) / 2
        const midY = (start.y + end.y) / 2

        // Perpendicular offset for curve
        const dx = end.x - start.x
        const dy = end.y - start.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        // Alternate curve direction based on index for variety
        const direction = i % 2 === 0 ? 1 : -1
        const perpX = (-dy / dist) * dist * curveAmount * direction
        const perpY = (dx / dist) * dist * curveAmount * direction

        const controlX = midX + perpX
        const controlY = midY + perpY

        // Path definition (Quadratic Bezier)
        const pathD = `M ${start.x} ${start.y} Q ${controlX} ${controlY} ${end.x} ${end.y}`

        // Label position at end of ray
        const labelPos = polarToCartesian(angle, maxRadius + 20, centerX, centerY)

        return (
          <g key={ray.id || i}>
            {/* Ray path with gradient */}
            <motion.path
              d={pathD}
              stroke={`url(#ray-gradient-${i})`}
              strokeWidth="1"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{
                pathLength: {
                  duration: animConfig.reducedMotion ? 0 : 0.8,
                  delay: animConfig.reducedMotion ? 0 : 0.4 + i * 0.03,
                  ease: [0.4, 0, 0.2, 1] as const,
                },
                opacity: {
                  duration: animConfig.reducedMotion ? 0 : 0.3,
                  delay: animConfig.reducedMotion ? 0 : 0.4 + i * 0.03,
                },
              }}
            />

            {/* Ray label - positioned outside (bubblenew-v2: optional vom Badge-Ring ersetzt) */}
            {!hideLabels && (
              <RayLabel
                label={ray.label}
                x={labelPos.x}
                y={labelPos.y}
                angle={angle}
                delay={animConfig.reducedMotion ? 0 : 0.6 + i * 0.03}
              />
            )}
          </g>
        )
      })}
    </svg>
  )
}

/**
 * Individual ray label - FIXED VERSION
 * Uses CSS class 'glass-chip' instead of CSS variables in inline styles
 */
interface RayLabelProps {
  label: string
  x: number
  y: number
  angle: number
  delay: number
}

function RayLabel({ label, x, y, delay }: RayLabelProps) {
  const animConfig = useAnimationConfig()

  const transition = animConfig.reducedMotion
    ? { duration: 0, delay }
    : { duration: 0.3, delay }

  return (
    <motion.foreignObject
      x={x - 40}
      y={y - 12}
      width="80"
      height="24"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={transition}
    >
      {/* Use CSS class for glass effects */}
      <div
        className="flex items-center justify-center w-full h-full glass-chip"
        style={{
          borderRadius: '12px',
          padding: '2px 8px',
        }}
      >
        <span
          className="text-[11px] font-medium text-gray-700 whitespace-nowrap"
          style={{
            textAlign: 'center',
          }}
        >
          {label}
        </span>
      </div>
    </motion.foreignObject>
  )
}
